import { GroupedGroupOption, SelectOptionType } from '@bp/ui-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { AscUnresolvedEntity, AscUnresolvedKind } from '../../../utils/timetable/timetableVersion';
import type { AscImportLabels } from '../hooks/useAscImportLabels';
import styles from './AscImportKindSlide.module.scss';
import { AscImportMappingRow } from './AscImportMappingRow';

type AscImportKindSlideBaseProps = {
  kind: AscUnresolvedKind;
  entries: AscUnresolvedEntity[];
  labels: AscImportLabels;
  /** Im Bericht steht der Hinweis einmal auf der Übersicht — je Art wäre er nur Rauschen. */
  showHint?: boolean;
};

/**
 * `mappable: false` gilt für LESSON/PERIOD (die ergeben sich aus den anderen Arten) und für den
 * Bericht nach dem Import. Als Union, damit die Zuordnungs-Props nur dort verlangt werden, wo es
 * tatsächlich etwas zuzuordnen gibt.
 */
type AscImportKindSlideProps = AscImportKindSlideBaseProps &
  (
    | {
        mappable: true;
        /** Nicht-Gruppen: flache Auswahlliste. */
        optionsFor: (entry: AscUnresolvedEntity) => SelectOptionType[];
        /** Nur GROUP: die nach Teilung verschachtelten Optionen für den `GroupPicker`. */
        groupOptionsFor: (entry: AscUnresolvedEntity) => GroupedGroupOption[];
        /** Nur GROUP: die Klasse, zu der die Gruppe gehört — sonst ist der Gruppenname nicht zuzuordnen. */
        classNameFor: (entry: AscUnresolvedEntity) => string | undefined;
        /** Nur GROUP: Teilung, auf die die Auswahl begrenzt ist (falls schon eine feststeht). */
        divisionNameFor: (entry: AscUnresolvedEntity) => string | undefined;
        mappings: Record<string, string | null>;
        onMappingChange: (entry: AscUnresolvedEntity, targetUuid: string | null | undefined) => void;
        busy: boolean;
      }
    | { mappable: false }
  );

/** Eine Folie je Art — „so heißt es in der Datei" → Auswahl bei uns. */
export const AscImportKindSlide: FC<AscImportKindSlideProps> = (props) => {
  const { kind, entries, labels, showHint = true } = props;
  const { t } = useTranslation();

  return (
    <div className={styles.content}>
      <div className={styles['section-title']}>
        {labels.kindLabel(kind)} ({entries.length})
      </div>
      {showHint && (
        <div className={styles['section-hint']}>
          {props.mappable
            ? t('pinboard.actions.ascImportMapping.hint')
            : t('pinboard.actions.ascImportReport.unresolvedHint')}
        </div>
      )}

      {props.mappable ? (
        <div className={styles.entries}>
          {entries.map((entry) => {
            const isGroup = kind === 'GROUP';
            return (
              <AscImportMappingRow
                key={`${kind}-${entry.ascId}`}
                entry={entry}
                options={isGroup ? undefined : props.optionsFor(entry)}
                groupedOptions={isGroup ? props.groupOptionsFor(entry) : undefined}
                classLabel={props.classNameFor(entry)}
                divisionName={props.divisionNameFor(entry)}
                grouped={isGroup}
                value={props.mappings[`${entry.kind}:${entry.ascId}`]}
                onChange={props.onMappingChange}
                disabled={props.busy}
              />
            );
          })}
        </div>
      ) : (
        <ul className={styles.entries}>
          {entries.map((entry) => (
            <li className={styles.entry} key={`${kind}-${entry.ascId}`}>
              <span className={styles['entry-label']}>{entry.label}</span>
              <span className={styles['entry-reason']}>{labels.reasonLabel(entry.reason)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
